/**
 * cave_light_material — the cave's rock / crystal materials that CONSUME the baked light field, and the local
 * reflection probe's capture (shader gaps program, agent A, 2026-09-29).
 *
 * OWNERSHIP (the report's §4.1/4.2: baked indirect, hemisphere fill and IBL get DISTINCT jobs — never summed
 * unattenuated). three r185's lighting model accumulates, per fragment: `irradiance` (the HemisphereLight — and in
 * Atlas nothing else), `iblIrradiance` + `radiance` (the scene's sky PMREM via EnvironmentNode), then
 * `PhysicalLightingModel.indirect()` turns them into indirect diffuse / specular and finally multiplies BOTH by the
 * context's `ambientOcclusion` (`material.aoNode` → AONode → `ambientOcclusion.mulAssign`, verified in
 * node_modules/three/src/nodes/lighting/AONode.js + functions/PhysicalLightingModel.js `ambientOcclusion()`: diffuse ×
 * ao, specular × Lagarde's roughness-aware specular occlusion). So `aoNode` WOULD scale a local term added to
 * `irradiance` too — it cannot carry "sky × visibility + local" by itself. This file therefore overrides the lighting
 * model's `indirect()` (it runs after EVERY light, whatever order three's id-sorted light list built them in —
 * `LightsNode.setupLightsNode` sorts scene lights and material lightings together by `.id`):
 *
 *   irradiance    ← (E_hemi − t_hook)/m_hook · T(n)   +   E_local(n)        (the hook's undo: cave_light_hook.ts)
 *   iblIrradiance ← iblIrradiance/m_env · T(n)
 *   radiance      ← radiance/m_env · specOcc(T)       +   the local probe (crystals, med+)
 *
 * T = the baked sky transfer, E_local = the crystals' baked irradiance, both from the probe grid (six RGBA16F 3D
 * textures, the ambient cube; cave_light.ts). The sky itself stays LIVE (time of day, weather, the moon): only the
 * geometry's visibility is baked. `direct()` gates the sun and the moon by T toward the light (a shadowless tier — and
 * VR — would otherwise light the cave floor through the hill); the cave's own point light is untouched.
 *
 * Everything is BLENDED by `uMix` (0 = exactly the plain material, 1 = the field): a field that arrives fades in over
 * ~1 s, a flag switched off fades out, and no exposure step ever reaches the meter.
 *
 * THE LOCAL PROBE (Lagarde & Zanuttini 2012, "Local image-based lighting with parallax-corrected cubemaps"): a
 * CubeCamera captures the cave's LOCAL radiance once per build — the rock under its baked E_local and the crystals'
 * emission, in a private scene with no sky, no sun and no fog (so the capture never bakes the time of day or the
 * display exposure, and no light joins the main scene: THE LIGHT-SET LAW), PMREM'd, and sampled by the crystals along
 * the reflection ray box-projected against the largest chamber. NOTE: three r185's own `getParallaxCorrectNormal`
 * computes the box as ±½·(cubeSize − cubePos) — right only for a box at the origin (the bpcem example's box is not);
 * this file carries a correct Lagarde intersection in render space.
 */
import * as THREE from 'three/webgpu';
import {
  texture3D, positionWorld, normalWorld, normalView, positionViewDirection, cameraWorldMatrix, roughness,
  vec3, float, uniform, max, min, select, mix, smoothstep, pmremTexture, Fn, step, clamp, texture as tslTexture, vec2, abs, normalize, pow,
} from 'three/tsl';
import { fieldToHalf, NORMAL_BIAS, type CaveLightField } from './cave_light.js';
import { uCaveHemiMul, uCaveHemiTint, uCaveEnvMul, CAVE_HOOK_MUL_FLOOR } from './cave_light_hook.js';
import { CAVE_ROCK_ALBEDO } from './cave_light_bake.js';
import { disposeLater } from './gpu_dispose.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL graphs are loosely typed
type N = any;

/** Build/diag counters (the lever and the harness read them). */
export const caveLightMaterialStats = { rockMaterials: 0, crystalMaterials: 0, lightingModels: 0, captures: 0, captureMs: 0 };

/** The rock's triplanar albedo (the cavity has no UVs): the highland texture along the dominant normal axis, damp and cool. */
export function caveRockAlbedo(tex: THREE.Texture): N {
  const wp = positionWorld;
  const n = abs(normalize(normalWorld));
  const w = pow(n, vec3(4, 4, 4));
  const wn = w.div(w.x.add(w.y).add(w.z));
  const k = float(1 / 6);
  const tx = tslTexture(tex, vec2(wp.z, wp.y).mul(k)).mul(wn.x);
  const ty = tslTexture(tex, vec2(wp.x, wp.z).mul(k)).mul(wn.y);
  const tz = tslTexture(tex, vec2(wp.x, wp.y).mul(k)).mul(wn.z);
  const albedo = tx.add(ty).add(tz);
  return mix(albedo.rgb.mul(vec3(0.55, 0.56, 0.6)), albedo.rgb.mul(0.8), float(0.35));
}

/** The GPU side of one cave's field: six RGBA16F 3D textures + the uniforms that place and blend it. */
export interface CaveFieldGpu {
  textures: THREE.Data3DTexture[];
  /** render-space position of probe (0,0,0) — the owner writes it every frame (the FP64 rebase moves it) */
  uOrigin: N; uSpacing: N; uDims: N; uBias: N;
  /** 0 = the plain material, 1 = the field (fades) */
  uMix: N;
  /** the crystals' baked irradiance (0 on the 'visibility' tier) */
  uLocal: N;
  /** the local reflection probe's weight */
  uProbe: N;
  /** the sun / moon gate (1 on; 0 = leave the direct light alone) */
  uSunGate: N;
  field: Pick<CaveLightField, 'origin' | 'spacing' | 'dims' | 'stats'>;
  bytes: number;
  dispose(): void;
}

/** Upload a baked field (half floats, face-major — `fieldToHalf`). */
export function createFieldGpu(field: Pick<CaveLightField, 'origin' | 'spacing' | 'dims' | 'stats'>, half: Uint16Array): CaveFieldGpu {
  const [nx, ny, nz] = field.dims;
  const P = nx * ny * nz;
  const textures: THREE.Data3DTexture[] = [];
  for (let f = 0; f < 6; f++) {
    const t = new THREE.Data3DTexture(new Uint16Array(half.buffer, half.byteOffset + f * P * 4 * 2, P * 4), nx, ny, nz);
    t.format = THREE.RGBAFormat; t.type = THREE.HalfFloatType;
    t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
    t.wrapS = t.wrapT = t.wrapR = THREE.ClampToEdgeWrapping;
    t.generateMipmaps = false; t.unpackAlignment = 1; t.flipY = false;
    t.name = `caveLightField:${f}`;
    t.needsUpdate = true;
    textures.push(t);
  }
  return {
    textures,
    uOrigin: uniform(new THREE.Vector3()), uSpacing: uniform(field.spacing), uDims: uniform(new THREE.Vector3(nx, ny, nz)),
    uBias: uniform(NORMAL_BIAS * field.spacing),
    uMix: uniform(0), uLocal: uniform(1), uProbe: uniform(0), uSunGate: uniform(1),
    field, bytes: half.byteLength,
    dispose() { for (const t of textures) t.dispose(); },
  };
}

/** Upload straight from a baked field (the harness / bed path). */
export function fieldGpuFrom(field: CaveLightField): CaveFieldGpu { return createFieldGpu(field, fieldToHalf(field)); }

/** The six face samples at this fragment, pushed along the normal (DDGI's normal bias). */
function sampleFaces(g: CaveFieldGpu): N[] {
  const q = positionWorld.add(normalWorld.mul(g.uBias));
  const uvw = q.sub(g.uOrigin).div(g.uSpacing).add(0.5).div(g.uDims);
  return g.textures.map((t) => texture3D(t, uvw));
}
/** Valve's ambient cube for direction n over the six face samples: Σ n_i² · C[sign n_i]. */
function cubeAt(f: N[], n: N): N {
  const n2 = n.mul(n);
  return select(n.x.greaterThanEqual(0), f[0], f[1]).mul(n2.x)
    .add(select(n.y.greaterThanEqual(0), f[2], f[3]).mul(n2.y))
    .add(select(n.z.greaterThanEqual(0), f[4], f[5]).mul(n2.z));
}

/** Lagarde's AABB projection (render space): the reflection ray's exit from the box, seen from the capture point. */
const parallaxBox = Fn(([dir, p, bmin, bmax, center]: N[]) => {
  const d = normalize(dir).toVar();
  const s = step(float(0), d).mul(2).sub(1);
  const safe = d.add(s.mul(1e-5));
  const t1 = bmax.sub(p).div(safe), t2 = bmin.sub(p).div(safe);
  const tf = max(t1, t2);
  const dist = max(min(tf.x, min(tf.y, tf.z)), float(0));
  return p.add(d.mul(dist)).sub(center);
});

/** One cave's local reflection probe on the GPU. */
export interface CaveProbeGpu {
  pmrem: THREE.Texture;
  uBoxMin: N; uBoxMax: N; uCenter: N;
  /** absolute (world) box + centre, for the owner's per-frame rebase write */
  boxAbs: { min: THREE.Vector3; max: THREE.Vector3; center: THREE.Vector3 };
  dispose(): void;
}

class CaveLightingModel extends THREE.PhysicalLightingModel {
  private faces: N[] = [];
  private fv: N = null;
  constructor(private readonly g: CaveFieldGpu, private readonly probe: CaveProbeGpu | null) { super(); caveLightMaterialStats.lightingModels++; }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- three's lighting-model inputs are loosely typed nodes
  start(builder: any): void {
    // THE LAZY-VAR LAW: sample once at the top of the lights stack, never inside a light's branch
    this.faces = sampleFaces(this.g).map((f) => f.toVar());
    this.fv = cubeAt(this.faces, normalWorld).toVar();
    super.start(builder);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  direct(lightData: any, builder: any): void {
    const light = lightData?.lightNode?.light as THREE.Light | undefined;
    if (light && (light as THREE.DirectionalLight).isDirectionalLight && this.faces.length === 6) {
      // THE SUN GATE: the sky transfer toward the light (view → world) — deep rock sees no sun through the hill
      const lw: N = lightData.lightDirection.transformDirection(cameraWorldMatrix);
      const tl: N = cubeAt(this.faces, lw).w;
      const gate: N = mix(float(1), smoothstep(0.02, 0.3, tl), this.g.uMix.mul(this.g.uSunGate));
      super.direct({ ...lightData, lightColor: lightData.lightColor.mul(gate) }, builder);
      return;
    }
    super.direct(lightData, builder);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  indirect(builder: any): void {
    if (!this.fv) { super.indirect(builder); return; }
    const ctx = builder.context;
    const g = this.g;
    const T: N = this.fv.w.clamp(0, 1);
    const E: N = this.fv.xyz.max(0);
    const k: N = g.uMix;
    const invHemi: N = float(1).div(max(uCaveHemiMul, float(CAVE_HOOK_MUL_FLOOR)));
    const invEnv: N = float(1).div(max(uCaveEnvMul, float(CAVE_HOOK_MUL_FLOOR)));
    // the hemisphere: undo the hook (m·E + t), then the baked transfer; + the crystals' baked light
    const hemiTrue: N = ctx.irradiance.sub(uCaveHemiTint).max(0).mul(invHemi);
    ctx.irradiance.assign(mix(ctx.irradiance, hemiTrue.mul(T), k).add(E.mul(g.uLocal).mul(k)));
    // the sky IBL's diffuse
    ctx.iblIrradiance.assign(mix(ctx.iblIrradiance, ctx.iblIrradiance.mul(invEnv).mul(T), k));
    // the sky IBL's specular under Lagarde's specular occlusion of T (three's own ambientOcclusion() form)
    const dotNV: N = normalView.dot(positionViewDirection).clamp();
    const aoExp: N = roughness.mul(-16.0).oneMinus().negate().exp2();
    const specT: N = T.sub(dotNV.add(T).pow(aoExp).oneMinus()).clamp();
    ctx.radiance.assign(mix(ctx.radiance, ctx.radiance.mul(invEnv).mul(specT), k));
    if (this.probe) {
      const p = this.probe;
      // three's radiance context: reflect(−V, N), bent toward N by roughness⁴, to world
      const rv: N = mix(positionViewDirection.negate().reflect(normalView), normalView, roughness.pow4()).normalize().transformDirection(cameraWorldMatrix);
      const dir: N = parallaxBox(rv, positionWorld, p.uBoxMin, p.uBoxMax, p.uCenter);
      const sampled: N = pmremTexture(p.pmrem, dir, roughness);
      // weight 1 inside the chamber's box, 0 by 4 m outside it; elsewhere the local radiance is the rock's under E_local
      const outside: N = max(max(p.uBoxMin.sub(positionWorld), positionWorld.sub(p.uBoxMax)), vec3(0));
      const w: N = clamp(float(1).sub(outside.length().div(4)), 0, 1);
      const fallback: N = E.mul(CAVE_ROCK_ALBEDO / Math.PI);
      ctx.radiance.addAssign(mix(fallback, sampled.rgb, w).mul(g.uLocal).mul(g.uProbe).mul(k));
    }
    super.indirect(builder);
  }
}

/** A MeshStandardNodeMaterial whose lighting model consumes one cave's field (+ probe). */
export class CaveLitMaterial extends THREE.MeshStandardNodeMaterial {
  static get type(): string { return 'CaveLitNodeMaterial'; }
  readonly caveField: CaveFieldGpu;
  readonly caveProbe: CaveProbeGpu | null;
  /** Keys the program per field (a node property's cache key is its id): two caves never share a build's bindings. */
  caveFieldNode: N;
  caveProbeNode: N;
  constructor(params: ConstructorParameters<typeof THREE.MeshStandardNodeMaterial>[0], field: CaveFieldGpu, probe: CaveProbeGpu | null) {
    super(params);
    this.caveField = field;
    this.caveProbe = probe;
    this.caveFieldNode = field.uMix;
    this.caveProbeNode = probe ? probe.uCenter : float(0);
  }
  setupLightingModel(): THREE.PhysicalLightingModel { return new CaveLightingModel(this.caveField, this.caveProbe); }
}

/** The rock of one cave. */
export function createCaveRockMaterial(field: CaveFieldGpu, albedo: N | null): CaveLitMaterial {
  const m = new CaveLitMaterial({ color: 0x5a5148, roughness: 0.92, metalness: 0.02, side: THREE.DoubleSide }, field, null);
  if (albedo) m.colorNode = albedo;
  m.name = 'caveRockLit';
  caveLightMaterialStats.rockMaterials++;
  return m;
}

/** The crystals of one cave (their emissive unchanged; their reflections from the local probe). */
export function createCaveCrystalMaterial(field: CaveFieldGpu, probe: CaveProbeGpu | null, emissiveHex: number, emissiveIntensity: number): CaveLitMaterial {
  const m = new CaveLitMaterial({ color: 0x8fd8ff, emissive: emissiveHex, emissiveIntensity, roughness: 0.25, metalness: 0.1 }, field, probe);
  m.name = 'caveCrystalLit';
  caveLightMaterialStats.crystalMaterials++;
  return m;
}

// ── THE LOCAL PROBE CAPTURE ─────────────────────────────────────────────────────────────────────────────────────────

/** A layer no main-scene camera renders: the capture meshes live on it in their private scene (belt and braces). */
export const CAVE_PROBE_LAYER = 29;
export const CAVE_PROBE_SIZE = 128;

export interface CaveCaptureInput {
  renderer: THREE.WebGPURenderer;
  field: CaveFieldGpu;
  /** the field's origin in the cave's LOCAL (mouth-relative) frame — the capture scene is local */
  fieldOriginLocal: readonly [number, number, number];
  geometry: THREE.BufferGeometry;
  /** the crystals' instanced geometry + matrices (local) */
  crystalGeometry: THREE.BufferGeometry;
  crystalMatrices: THREE.Matrix4[];
  albedo: N | null;
  crystalRadiance: readonly [number, number, number];
  /** the capture point and the chamber's box, LOCAL */
  center: THREE.Vector3;
  boxMin: THREE.Vector3;
  boxMax: THREE.Vector3;
}

/**
 * Capture a cave's LOCAL radiance into a cube map and PMREM it. The private scene holds unlit copies of the rock
 * (albedo × E_local / π from the field — no sky, no sun) and the crystals (their emission); background black.
 * Linear HDR: a render into a target is never tone mapped (`Renderer.currentToneMapping` is NoToneMapping unless the
 * target IS the output), so the display exposure never enters the probe.
 */
export function captureCaveProbe(inp: CaveCaptureInput): { pmrem: THREE.RenderTarget; ms: number } {
  const t0 = performance.now();
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0, 0, 0);
  // the capture's own field sampling: the same textures, the LOCAL origin
  const g: CaveFieldGpu = { ...inp.field, uOrigin: uniform(new THREE.Vector3(...inp.fieldOriginLocal)) };
  const f = sampleFaces(g);
  const cube = cubeAt(f, normalWorld);
  const rockMat = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  const alb: N = inp.albedo ?? vec3(0.2, 0.19, 0.18);
  rockMat.colorNode = alb.mul(cube.xyz.max(0)).mul(1 / Math.PI);
  const crystalMat = new THREE.MeshBasicNodeMaterial();
  crystalMat.colorNode = vec3(inp.crystalRadiance[0], inp.crystalRadiance[1], inp.crystalRadiance[2]);
  const rock = new THREE.Mesh(inp.geometry, rockMat);
  rock.layers.set(CAVE_PROBE_LAYER);
  scene.add(rock);
  const n = inp.crystalMatrices.length;
  const crystals = new THREE.InstancedMesh(inp.crystalGeometry, crystalMat, Math.max(1, n));
  for (let i = 0; i < n; i++) crystals.setMatrixAt(i, inp.crystalMatrices[i]);
  crystals.count = n;
  crystals.layers.set(CAVE_PROBE_LAYER);
  scene.add(crystals);
  // three r185 exports CubeRenderTarget from three/webgpu (Three.WebGPU.js); @types/three 0.182 does not declare it
  const CubeRT = (THREE as unknown as { CubeRenderTarget: new (size: number, o: object) => THREE.RenderTarget & { texture: THREE.CubeTexture } }).CubeRenderTarget;
  const rt = new CubeRT(CAVE_PROBE_SIZE, { type: THREE.HalfFloatType, generateMipmaps: false });
  const cam = new THREE.CubeCamera(0.05, 400, rt as never);
  cam.layers.set(CAVE_PROBE_LAYER);
  for (const c of cam.children) c.layers.set(CAVE_PROBE_LAYER);
  cam.position.copy(inp.center);
  scene.add(cam);
  scene.updateMatrixWorld(true);
  cam.update(inp.renderer as never, scene);
  const gen = new THREE.PMREMGenerator(inp.renderer as never);
  const pm = gen.fromCubemap(rt.texture as never) as unknown as THREE.RenderTarget;
  gen.dispose();
  // the submitted capture still reads these: free them after the submit window (THE DEFERRED-DISPOSE LAW)
  disposeLater(rt as never); disposeLater(rockMat); disposeLater(crystalMat); disposeLater(crystals as never);
  const ms = performance.now() - t0;
  caveLightMaterialStats.captures++; caveLightMaterialStats.captureMs = ms;
  return { pmrem: pm, ms };
}

/** The probe's GPU handle (render-space box uniforms the owner rebases every frame). */
export function createProbeGpu(pmrem: THREE.RenderTarget, boxAbs: { min: THREE.Vector3; max: THREE.Vector3; center: THREE.Vector3 }): CaveProbeGpu {
  return {
    pmrem: pmrem.texture,
    uBoxMin: uniform(new THREE.Vector3()), uBoxMax: uniform(new THREE.Vector3()), uCenter: uniform(new THREE.Vector3()),
    boxAbs,
    dispose() { pmrem.dispose(); },
  };
}

/** Write the render-space uniforms (absolute + scene.position — THE FP64 REBASE: scene children are absolute). */
export function rebaseCaveUniforms(g: CaveFieldGpu, originAbs: readonly [number, number, number], p: CaveProbeGpu | null, shift: { x: number; y: number; z: number }): void {
  (g.uOrigin.value as THREE.Vector3).set(originAbs[0] + shift.x, originAbs[1] + shift.y, originAbs[2] + shift.z);
  if (p) {
    (p.uBoxMin.value as THREE.Vector3).set(p.boxAbs.min.x + shift.x, p.boxAbs.min.y + shift.y, p.boxAbs.min.z + shift.z);
    (p.uBoxMax.value as THREE.Vector3).set(p.boxAbs.max.x + shift.x, p.boxAbs.max.y + shift.y, p.boxAbs.max.z + shift.z);
    (p.uCenter.value as THREE.Vector3).set(p.boxAbs.center.x + shift.x, p.boxAbs.center.y + shift.y, p.boxAbs.center.z + shift.z);
  }
}
